// Copyright (c) 2026 TRIVONT IT Private Limited. All rights reserved.
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, AlertCircle, Settings, GitBranch, CheckCircle, Clock, Shield, Zap, Activity, ArrowRight, Smartphone, RefreshCw, QrCode } from 'lucide-react';
import { authAPI, mfaAPI } from '../services/api';import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// ─── Animated ITSM stat card ──────────────────────────────────────────────────
function StatCard({ icon: Icon, color, bg, title, value, subtitle, delay = 0, pulse = false }) {
  return (
    <div
      style={{
        animation: `floatCard 6s ease-in-out infinite`,
        animationDelay: `${delay}s`,
      }}
      className={`${bg} backdrop-blur-sm border border-white/10 rounded-2xl px-4 py-3 flex items-center gap-3 w-64 shadow-xl`}
    >
      <div className={`${color} w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-white font-bold text-base">{value}</span>
          {pulse && <span className="w-2 h-2 bg-rose-400 rounded-full animate-pulse" />}
        </div>
        <div className="text-white/60 text-xs truncate">{title}</div>
        {subtitle && <div className="text-white/40 text-xs">{subtitle}</div>}
      </div>
    </div>
  );
}

// ─── Animated ticket row ──────────────────────────────────────────────────────
function TicketRow({ id, title, status, priority, delay = 0 }) {
  const statusColor = { new: 'bg-slate-500', assigned: 'bg-blue-500', in_progress: 'bg-emerald-500', pending_approval: 'bg-purple-500' };
  const priorityColor = { P1: 'text-rose-400', P2: 'text-amber-400', P3: 'text-blue-400', P4: 'text-slate-400' };
  return (
    <div
      style={{ animation: `slideInRight 0.5s ease forwards ${delay}s`, opacity: 0 }}
      className="flex items-center gap-3 bg-white/5 rounded-xl px-3 py-2.5 border border-white/5"
    >
      <span className={`text-xs font-bold ${priorityColor[priority]}`}>{priority}</span>
      <span className="text-white/80 text-xs flex-1 truncate">{title}</span>
      <span className="font-mono text-white/40 text-xs">{id}</span>
      <span className={`w-1.5 h-1.5 rounded-full ${statusColor[status] || 'bg-slate-500'}`} />
    </div>
  );
}

// ─── SLA Progress Bar ─────────────────────────────────────────────────────────
function SLABar({ label, value, color }) {
  return (
    <div>
      <div className="flex justify-between text-xs text-white/50 mb-1">
        <span>{label}</span><span className="font-semibold text-white/70">{value}%</span>
      </div>
      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div
          className={`h-full ${color} rounded-full`}
          style={{ width: `${value}%`, animation: 'growBar 1.5s ease forwards', transformOrigin: 'left' }}
        />
      </div>
    </div>
  );
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [branding, setBranding] = useState({ app_name: 'SupportOne', tenant_name: 'TRIVONT', logo_url: null });
  // MFA state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [mfaMethod, setMfaMethod] = useState('authenticator');
  const [emailHint, setEmailHint] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  // First-login forced MFA setup
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  const [setupStep, setSetupStep] = useState('totp'); // totp | email
  const [setupTempToken, setSetupTempToken] = useState('');
  const [setupEmail, setSetupEmail] = useState('');
  const [setupUserName, setSetupUserName] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [totpConfirmed, setTotpConfirmed] = useState(false);
  const [setupOtp, setSetupOtp] = useState('');

  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setTimeout(() => setMounted(true), 100);
    // Fetch tenant branding (public endpoint, no auth needed)
    import('../services/api').then(({ api: apiClient }) => {
      apiClient.get('/tenants/branding/').then(r => {
        if (r.data) setBranding(r.data);
      }).catch(() => {});
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Enter email and password'); return; }
    setLoading(true);
    try {
      const res = await authAPI.login({ email, password });
      if (res.data.mfa_setup_required) {
        // First login — forced MFA setup wizard
        setSetupTempToken(res.data.temp_token);
        setSetupEmail(res.data.user_email);
        setSetupUserName(res.data.user_name || '');
        setMfaSetupRequired(true);
        setSetupStep('totp');
        // Auto-fetch QR code
        const qrRes = await mfaAPI.firstLoginSetup({ temp_token: res.data.temp_token, step: 'get_totp' });
        setQrCode(qrRes.data.qr_code);
        setTotpSecret(qrRes.data.manual_entry);
      } else if (res.data.mfa_required) {
        // MFA challenge
        setTempToken(res.data.temp_token);
        setMfaMethod(res.data.mfa_method || 'authenticator');
        setEmailHint(res.data.user_email || '');  // full email
        setMfaRequired(true);
        if (res.data.mfa_method === 'email') {
          // Auto-send email OTP
          await mfaAPI.sendEmailOtp({ temp_token: res.data.temp_token });
          setOtpSent(true);
          toast.success('OTP sent to your email');
        }
      } else {
        const tokens = res.data.tokens || { access: res.data.access, refresh: res.data.refresh };
        login(res.data.user, tokens);
        navigate('/', { replace: true });
      }
    } catch {
      toast.error('Invalid credentials. Please try again.');
    } finally { setLoading(false); }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    if (!otp.trim()) { toast.error('Enter the OTP code'); return; }
    setLoading(true);
    try {
      const res = await mfaAPI.verify({ temp_token: tempToken, otp: otp.trim(), method: mfaMethod });
      const tokens = res.data.tokens;
      login(res.data.user, tokens);
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Invalid OTP. Please try again.');
    } finally { setLoading(false); }
  };

  const handleSendEmailOtp = async () => {
    setLoading(true);
    try {
      await mfaAPI.sendEmailOtp({ temp_token: tempToken });
      setOtpSent(true);
      toast.success('OTP sent to your email');
    } catch { toast.error('Failed to send OTP'); }
    finally { setLoading(false); }
  };

  const switchMfaMethod = (newMethod) => {
    setMfaMethod(newMethod);
    setOtp('');
    if (newMethod === 'email') handleSendEmailOtp();
  };

  // ── First-login MFA setup handlers ──────────────────────────────────────────
  const handleConfirmTotp = async () => {
    if (!setupOtp.trim()) { toast.error('Enter the code from your app'); return; }
    setLoading(true);
    try {
      await mfaAPI.firstLoginSetup({ temp_token: setupTempToken, step: 'confirm_totp', code: setupOtp });
      setTotpConfirmed(true);
      setSetupStep('email');
      setSetupOtp('');
      // Auto-send email OTP
      const r = await mfaAPI.firstLoginSetup({ temp_token: setupTempToken, step: 'send_email_otp' });
      toast.success(`OTP sent to ${r.data.user_email}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Invalid code. Try again.');
    } finally { setLoading(false); }
  };

  const handleResendEmailOtp = async () => {
    setLoading(true);
    try {
      const r = await mfaAPI.firstLoginSetup({ temp_token: setupTempToken, step: 'send_email_otp' });
      toast.success(`OTP sent to ${r.data.user_email}`);
    } catch { toast.error('Failed to send OTP'); }
    finally { setLoading(false); }
  };

  const handleConfirmEmailOtp = async () => {
    if (!setupOtp.trim()) { toast.error('Enter the OTP'); return; }
    setLoading(true);
    try {
      const res = await mfaAPI.firstLoginSetup({ temp_token: setupTempToken, step: 'confirm_email', code: setupOtp });
      login(res.data.user, res.data.tokens);
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Invalid OTP. Try again.');
    } finally { setLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('Enter your email first'); return; }
    try {
      await authAPI.forgotPassword({ email });
      toast.success('Password reset email sent');
    } catch { toast.error('Failed to send reset email'); }
  };

  return (
    <>
      <style>{`
        @keyframes floatCard {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-10px); }
        }
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(20px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes growBar {
          from { width: 0%; }
        }
        @keyframes pulse-ring {
          0%   { transform: scale(1);   opacity: 0.8; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        @keyframes orbit {
          from { transform: rotate(0deg) translateX(120px) rotate(0deg); }
          to   { transform: rotate(360deg) translateX(120px) rotate(-360deg); }
        }
        @keyframes bgShift {
          0%,100% { background-position: 0% 50%; }
          50%     { background-position: 100% 50%; }
        }
        .login-panel { animation: fadeInUp 0.7s ease forwards; }
        .stat-delay-1 { animation-delay: 0.2s; }
        .stat-delay-2 { animation-delay: 0.4s; }
        .stat-delay-3 { animation-delay: 0.6s; }
      `}</style>

      <div className="min-h-screen flex">

        {/* ── LEFT PANEL — ITSM Dashboard Preview ─────────────────────────── */}
        <div
          className="hidden lg:flex flex-col justify-center px-10 xl:px-14 w-1/2 relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #0c1a3b 70%, #1a0a2e 100%)',
            backgroundSize: '400% 400%',
            animation: 'bgShift 12s ease infinite',
          }}
        >
          {/* Background grid */}
          <div className="absolute inset-0 opacity-10"
            style={{ backgroundImage: 'radial-gradient(circle, #38bdf8 1px, transparent 1px)', backgroundSize: '32px 32px' }} />

          {/* Glow orbs */}
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-sky-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-violet-500/10 rounded-full blur-3xl" />

          <div className="relative z-10">
            {/* Header */}
            <div className="mb-10" style={{ opacity: mounted ? 1 : 0, transition: 'opacity 0.8s 0.1s' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-sky-500 rounded-xl flex items-center justify-center font-black text-white">T</div>
                <span className="text-white font-bold text-xl">TRIVONT SupportOne</span>
              </div>
              <h2 className="text-3xl xl:text-4xl font-bold text-white leading-snug mb-2">
                IT Service Management<br /><span className="text-sky-400">at your fingertips</span>
              </h2>
              <p className="text-white/50 text-sm">ITIL-aligned · Real-time SLA · Auto-escalation · Multi-tenant</p>
            </div>

            {/* Live stats row */}
            <div
              className="flex flex-wrap gap-3 mb-8"
              style={{ opacity: mounted ? 1 : 0, transition: 'opacity 0.8s 0.4s' }}
            >
              <StatCard icon={AlertCircle} color="bg-rose-500" bg="bg-white/8" title="Active Incidents" value="12" subtitle="3 × P1 critical" delay={0} pulse />
              <StatCard icon={CheckCircle} color="bg-emerald-500" bg="bg-white/8" title="SLA Compliance" value="94.2%" subtitle="↑ 2.1% this week" delay={2} />
              <StatCard icon={Clock} color="bg-amber-500" bg="bg-white/8" title="Avg Resolution" value="4.2h" subtitle="business hours" delay={4} />
            </div>

            {/* SLA bars */}
            <div
              className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-6 w-80"
              style={{ opacity: mounted ? 1 : 0, transition: 'opacity 0.8s 0.7s' }}
            >
              <div className="flex items-center gap-2 mb-3">
                <Activity className="w-4 h-4 text-sky-400" />
                <span className="text-white/70 text-xs font-semibold uppercase tracking-wide">SLA Compliance Today</span>
              </div>
              <div className="space-y-2.5">
                <SLABar label="P1 Critical" value={88} color="bg-rose-500" />
                <SLABar label="P2 High" value={93} color="bg-amber-500" />
                <SLABar label="P3 Medium" value={97} color="bg-emerald-500" />
                <SLABar label="Service Requests" value={99} color="bg-sky-500" />
              </div>
            </div>

            {/* Recent tickets */}
            <div
              className="w-80 space-y-1.5"
              style={{ opacity: mounted ? 1 : 0, transition: 'opacity 0.8s 1s' }}
            >
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-sky-400" />
                <span className="text-white/70 text-xs font-semibold uppercase tracking-wide">Live Ticket Queue</span>
              </div>
              <TicketRow id="INC-0159" priority="P1" title="Payment gateway 500 errors" status="in_progress" delay={1.0} />
              <TicketRow id="SR-0065" priority="P2" title="VPN access request — contractor" status="pending_approval" delay={1.3} />
              <TicketRow id="INC-0157" priority="P3" title="Email server slow response" status="assigned" delay={1.6} />
              <TicketRow id="CR-0035" priority="P2" title="Firewall upgrade to v7.0" status="in_progress" delay={1.9} />
            </div>

            {/* Feature chips */}
            <div
              className="flex flex-wrap gap-2 mt-8"
              style={{ opacity: mounted ? 1 : 0, transition: 'opacity 0.8s 1.4s' }}
            >
              {['Auto SLA Pause', 'Multi-level Approvals', 'Smart Escalation', 'Business Hours Mode', 'Shift Calendar'].map(f => (
                <span key={f} className="bg-white/5 border border-white/10 text-white/50 text-xs px-3 py-1 rounded-full">{f}</span>
              ))}
            </div>
          </div>
        </div>

        {/* ── RIGHT PANEL — Login Form ─────────────────────────────────────── */}
        <div className="flex-1 lg:flex-none lg:w-1/2 flex flex-col justify-center items-center px-8 sm:px-14 xl:px-20 py-12 bg-slate-950 lg:bg-slate-900">
          <div
            className="login-panel w-full max-w-md"
            style={{ opacity: 0 }}
          >
            {/* Mobile logo */}
            <div className="text-center mb-8 lg:mb-10">
              {branding.logo_url ? (
                <img src={branding.logo_url} alt="Logo" className="w-16 h-16 rounded-2xl object-contain mx-auto mb-4 border border-slate-700 bg-white p-1.5 shadow-lg" />
              ) : (
                <div className="w-14 h-14 bg-sky-500 rounded-2xl flex items-center justify-center font-black text-white text-2xl mx-auto mb-4 shadow-lg shadow-sky-500/30">
                  {branding.tenant_name?.charAt(0) || 'T'}
                </div>
              )}
              <h1 className="text-2xl font-bold text-white mb-1">{branding.tenant_name} {branding.app_name}</h1>
              <p className="text-sky-400 text-sm">Simplified and Redefined</p>
            </div>

            {/* ── First-login MFA Setup Wizard ── */}
            {mfaSetupRequired ? (
              <div className="space-y-5">
                {/* Progress */}
                <div className="flex items-center gap-3 mb-2">
                  <div className={`flex items-center gap-1.5 text-xs font-semibold ${setupStep === 'totp' || totpConfirmed ? 'text-sky-400' : 'text-slate-500'}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${totpConfirmed ? 'bg-emerald-500 text-white' : 'bg-sky-500 text-white'}`}>
                      {totpConfirmed ? '✓' : '1'}
                    </div>
                    Authenticator
                  </div>
                  <div className="h-px flex-1 bg-slate-600" />
                  <div className={`flex items-center gap-1.5 text-xs font-semibold ${setupStep === 'email' ? 'text-sky-400' : 'text-slate-600'}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${setupStep === 'email' ? 'bg-sky-500 text-white' : 'bg-slate-700 text-slate-400'}`}>2</div>
                    Email OTP
                  </div>
                </div>

                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3">
                  <p className="text-amber-400 text-xs font-semibold">MFA SETUP REQUIRED</p>
                  <p className="text-slate-300 text-xs mt-1">Hi {setupUserName}, your admin requires you to set up two-factor authentication before accessing the system.</p>
                </div>

                {setupStep === 'totp' && (
                  <div className="space-y-4">
                    <div>
                      <p className="text-white text-sm font-semibold mb-1">Step 1: Set up Authenticator App</p>
                      <p className="text-slate-400 text-xs">Scan this QR code with Google Authenticator or Authy.</p>
                    </div>
                    {qrCode && (
                      <div className="flex gap-3 items-start">
                        <img src={qrCode} alt="QR Code" className="w-28 h-28 rounded-lg border border-slate-600 bg-white p-1 flex-shrink-0" />
                        <div className="flex-1 space-y-1">
                          <p className="text-xs text-slate-400">Can't scan? Enter manually:</p>
                          <code className="text-xs bg-slate-900 px-2 py-1.5 rounded-lg text-slate-300 block break-all font-mono">{totpSecret}</code>
                        </div>
                      </div>
                    )}
                    <div>
                      <label className="text-xs text-slate-400 block mb-1.5">Enter 6-digit code from app</label>
                      <input type="text" inputMode="numeric" maxLength={6} autoFocus
                        value={setupOtp} onChange={e => setSetupOtp(e.target.value.replace(/\D/g, ''))}
                        placeholder="000000"
                        className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-3 text-center font-mono text-lg tracking-widest focus:outline-none focus:border-sky-500"
                        data-testid="setup-totp-input"
                      />
                    </div>
                    <button onClick={handleConfirmTotp} disabled={loading || setupOtp.length !== 6}
                      className="w-full bg-sky-500 hover:bg-sky-400 disabled:bg-sky-800 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2"
                      data-testid="confirm-totp-btn">
                      {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><CheckCircle className="w-4 h-4" /> Confirm & Continue</>}
                    </button>
                  </div>
                )}

                {setupStep === 'email' && (
                  <div className="space-y-4">
                    <div>
                      <p className="text-white text-sm font-semibold mb-1">Step 2: Confirm Email OTP</p>
                      <p className="text-slate-400 text-xs">A verification code was sent to <span className="text-sky-400 font-medium">{setupEmail}</span></p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 block mb-1.5">Enter 6-digit OTP from email</label>
                      <input type="text" inputMode="numeric" maxLength={6} autoFocus
                        value={setupOtp} onChange={e => setSetupOtp(e.target.value.replace(/\D/g, ''))}
                        placeholder="000000"
                        className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-3 text-center font-mono text-lg tracking-widest focus:outline-none focus:border-sky-500"
                        data-testid="setup-email-otp-input"
                      />
                    </div>
                    <button onClick={handleConfirmEmailOtp} disabled={loading || setupOtp.length !== 6}
                      className="w-full bg-sky-500 hover:bg-sky-400 disabled:bg-sky-800 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2"
                      data-testid="confirm-email-otp-btn">
                      {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><CheckCircle className="w-4 h-4" /> Complete Setup & Sign In</>}
                    </button>
                    <button onClick={handleResendEmailOtp} disabled={loading} className="w-full text-sky-400 hover:text-sky-300 text-xs py-1 flex items-center justify-center gap-1">
                      <RefreshCw className="w-3.5 h-3.5" /> Resend OTP to {setupEmail}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <div className="mb-6">
                  <h2 className="text-xl font-bold text-white mb-1">Sign In</h2>
                  <p className="text-slate-400 text-sm">Enter your credentials to continue</p>
                </div>
                {mfaRequired ? (
              <form onSubmit={handleMfaVerify} className="space-y-4">
                <div className="mb-1">
                  <h2 className="text-xl font-bold text-white mb-0.5">Two-Factor Verification</h2>
                  <p className="text-slate-400 text-xs">Choose how you'd like to verify your identity</p>
                </div>
                {/* ── Method selector tabs ── */}
                <div className="flex gap-2 p-1 bg-slate-800 rounded-xl mb-1">
                  <button type="button"
                    onClick={() => { if (mfaMethod !== 'authenticator') { setMfaMethod('authenticator'); setOtp(''); } }}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${
                      mfaMethod === 'authenticator'
                        ? 'bg-sky-500 text-white shadow'
                        : 'text-slate-400 hover:text-slate-300'
                    }`}
                    data-testid="method-authenticator-tab"
                  >
                    <Smartphone className="w-3.5 h-3.5" /> Authenticator App
                  </button>
                  <button type="button"
                    onClick={() => { if (mfaMethod !== 'email') switchMfaMethod('email'); }}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${
                      mfaMethod === 'email'
                        ? 'bg-sky-500 text-white shadow'
                        : 'text-slate-400 hover:text-slate-300'
                    }`}
                    data-testid="method-email-tab"
                  >
                    <Mail className="w-3.5 h-3.5" /> Email OTP
                  </button>
                </div>

                {/* Context message */}
                <div className={`flex items-start gap-2.5 rounded-xl px-3 py-2.5 text-xs ${
                  mfaMethod === 'authenticator'
                    ? 'bg-slate-800 border border-slate-700 text-slate-400'
                    : 'bg-sky-500/10 border border-sky-500/20 text-sky-300'
                }`}>
                  {mfaMethod === 'authenticator' ? (
                    <>
                      <Smartphone className="w-4 h-4 mt-0.5 flex-shrink-0 text-slate-500" />
                      <span>Open your authenticator app and enter the 6-digit code.</span>
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mt-0.5 flex-shrink-0 text-sky-400" />
                      <span>
                        OTP sent to <span className="font-semibold text-white">{emailHint}</span>.
                        {' '}<button type="button" onClick={handleSendEmailOtp} disabled={loading} className="text-sky-400 hover:text-sky-300 underline underline-offset-2">Resend</button>
                      </span>
                    </>
                  )}
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-400 block mb-1.5">
                    {mfaMethod === 'authenticator' ? '6-digit code from app' : `6-digit OTP sent to ${emailHint}`}
                  </label>
                  <input
                    type="text" inputMode="numeric" maxLength={6} autoFocus
                    placeholder="000000"
                    value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                    className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-3 text-sm placeholder:text-slate-500 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-all tracking-widest text-center font-mono text-lg"
                    data-testid="otp-input"
                  />
                </div>

                <button type="submit" disabled={loading}
                  className="w-full bg-sky-500 hover:bg-sky-400 disabled:bg-sky-700 text-white font-semibold py-3 rounded-xl transition-all flex items-center justify-center gap-2 group shadow-lg shadow-sky-500/20"
                  data-testid="mfa-verify-btn">
                  {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <>Verify & Sign In <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></>}
                </button>

                {/* Lost phone note */}
                <p className="text-xs text-slate-600 text-center">
                  Lost your phone? Use the <button type="button" onClick={() => switchMfaMethod('email')} className="text-sky-500 hover:text-sky-400 underline underline-offset-2">Email OTP</button> tab above.
                </p>

                <div className="text-center">
                  <button type="button" onClick={() => { setMfaRequired(false); setOtp(''); setTempToken(''); }} className="text-slate-600 hover:text-slate-400 text-xs transition-colors">
                    ← Back to login
                  </button>
                </div>
              </form>
                ) : (
                  /* ── Normal Login Step ── */
                  <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-slate-400 block mb-1.5">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type="email" autoComplete="email"
                      placeholder="Enter your email address"
                      value={email} onChange={e => setEmail(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl pl-10 pr-4 py-3 text-sm placeholder:text-slate-500 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-all"
                      data-testid="email-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-400 block mb-1.5">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type={showPassword ? 'text' : 'password'} autoComplete="current-password"
                      placeholder="Enter password"
                      value={password} onChange={e => setPassword(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl pl-10 pr-11 py-3 text-sm placeholder:text-slate-500 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-all"
                      data-testid="password-input"
                    />
                    <button type="button" onClick={() => setShowPassword(p => !p)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <button type="submit" disabled={loading}
                  className="w-full bg-sky-500 hover:bg-sky-400 disabled:bg-sky-700 text-white font-semibold py-3 rounded-xl transition-all flex items-center justify-center gap-2 group shadow-lg shadow-sky-500/20"
                  data-testid="login-btn">
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>Sign In <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></>
                  )}
                </button>
              </form>
                )}
                {!mfaRequired && (
                  <div className="text-center mt-4">
                    <button onClick={handleForgotPassword} className="text-sky-400 hover:text-sky-300 text-sm transition-colors">
                      Forgot Password?
                    </button>
                  </div>
                )}
              </div>
            )}

            <p className="text-center text-slate-600 text-xs mt-8">
              Contact your system administrator if you need access.
            </p>

            {/* Feature highlights — mobile */}
            <div className="lg:hidden grid grid-cols-2 gap-2 mt-8">
              {[
                { icon: Shield, label: 'ITIL Aligned' },
                { icon: Zap, label: 'Real-time SLA' },
                { icon: Activity, label: 'Auto Escalation' },
                { icon: Clock, label: 'Business Hours' },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="bg-slate-800/50 border border-slate-700 rounded-xl p-3 flex items-center gap-2">
                  <Icon className="w-4 h-4 text-sky-500 flex-shrink-0" />
                  <span className="text-slate-400 text-xs">{label}</span>
                </div>
              ))}
            </div>
          </div>

          <p className="text-slate-700 text-xs mt-10">
            Copyright © 2026 TRIVONT IT Private Limited. All rights reserved.
          </p>
        </div>

      </div>
    </>
  );
}
